import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Search, 
  Filter, 
  Calendar,
  Target,
  TrendingUp,
  TrendingDown,
  Award,
  BookOpen,
  BarChart3,
  Activity,
  CheckCircle,
  XCircle,
  Edit,
  Trash2,
  Eye,
  X,
  LineChart,
  Trophy,
  Users,
  Zap,
  Star,
  ChevronRight,
  Edit3,
  Calculator,
  FileText,
  Save
} from 'lucide-react';

// Interfaces
interface ExamResult {
  id: string;
  examName: string;
  date: string;
  type: 'LGS' | 'Deneme';
  institution?: string;
  scores: {
    'Türkçe': number;
    'Matematik': number;
    'Fen Bilimleri': number;
    'T.C. İnkılap Tarihi': number;
    'İngilizce': number;
    'Din Kültürü': number;
  };
  correctAnswers: {
    'Türkçe': number;
    'Matematik': number;
    'Fen Bilimleri': number;
    'T.C. İnkılap Tarihi': number;
    'İngilizce': number;
    'Din Kültürü': number;
  };
  wrongAnswers: {
    'Türkçe': number;
    'Matematik': number;
    'Fen Bilimleri': number;
    'T.C. İnkılap Tarihi': number;
    'İngilizce': number;
    'Din Kültürü': number;
  };
  totalScore: number;
  ranking?: number;
  totalParticipants?: number;
}

interface ExamStats {
  totalExams: number;
  averageScore: number;
  bestScore: number;
  worstScore: number;
  improvementRate: number;
  subjectAverages: {
    [key: string]: number;
  };
}

const SUBJECTS = ['Türkçe', 'Matematik', 'Fen Bilimleri', 'T.C. İnkılap Tarihi', 'İngilizce', 'Din Kültürü'];

// Resmi LGS Scoring System (2024)
const LGS_SCORING = {
  'Türkçe': { coefficient: 4.349, totalQuestions: 20, maxScore: 86.98 }, // 20 * 4.349
  'Matematik': { coefficient: 4.254, totalQuestions: 20, maxScore: 85.08 }, // 20 * 4.254
  'Fen Bilimleri': { coefficient: 4.123, totalQuestions: 20, maxScore: 82.46 }, // 20 * 4.123
  'T.C. İnkılap Tarihi': { coefficient: 1.667, totalQuestions: 10, maxScore: 16.67 }, // 10 * 1.667
  'İngilizce': { coefficient: 1.508, totalQuestions: 10, maxScore: 15.08 }, // 10 * 1.508
  'Din Kültürü': { coefficient: 1.899, totalQuestions: 10, maxScore: 18.99 } // 10 * 1.899
};

// LGS Taban Puanı
const LGS_BASE_SCORE = 194.752;

const SUBJECT_COLORS = {
  'Türkçe': 'from-red-500 to-pink-500',
  'Matematik': 'from-blue-500 to-cyan-500',
  'Fen Bilimleri': 'from-green-500 to-emerald-500',
  'T.C. İnkılap Tarihi': 'from-purple-500 to-violet-500',
  'İngilizce': 'from-yellow-500 to-orange-500',
  'Din Kültürü': 'from-indigo-500 to-blue-500'
};

const SUBJECT_ICONS = {
  'Türkçe': BookOpen,
  'Matematik': BarChart3,
  'Fen Bilimleri': Zap,
  'T.C. İnkılap Tarihi': Award,
  'İngilizce': Star,
  'Din Kültürü': Target
};

const ExamResults: React.FC = () => {
  const [examResults, setExamResults] = useState<ExamResult[]>([]);
  const [filteredResults, setFilteredResults] = useState<ExamResult[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('Tümü');
  const [showAddModal, setShowAddModal] = useState(false);
  const [activeTab, setActiveTab] = useState<'results' | 'stats' | 'chart'>('results');
  const [stats, setStats] = useState<ExamStats>({
    totalExams: 0,
    averageScore: 0,
    bestScore: 0,
    worstScore: 0,
    improvementRate: 0,
    subjectAverages: {}
  });

  const [newResult, setNewResult] = useState<Omit<ExamResult, 'id' | 'totalScore'>>({
    examName: '',
    date: new Date().toISOString().split('T')[0],
    type: 'Deneme',
    institution: '',
    scores: {
      'Türkçe': 0,
      'Matematik': 0,
      'Fen Bilimleri': 0,
      'T.C. İnkılap Tarihi': 0,
      'İngilizce': 0,
      'Din Kültürü': 0
    },
    correctAnswers: {
      'Türkçe': 0,
      'Matematik': 0,
      'Fen Bilimleri': 0,
      'T.C. İnkılap Tarihi': 0,
      'İngilizce': 0,
      'Din Kültürü': 0
    },
    wrongAnswers: {
      'Türkçe': 0,
      'Matematik': 0,
      'Fen Bilimleri': 0,
      'T.C. İnkılap Tarihi': 0,
      'İngilizce': 0,
      'Din Kültürü': 0
    },
    ranking: undefined,
    totalParticipants: undefined
  });

  // Resmi LGS Puan Hesaplama Fonksiyonu
  const calculateLGSScore = (correct: number, wrong: number, subject: string): number => {
    const subjectInfo = LGS_SCORING[subject as keyof typeof LGS_SCORING];
    
    // Validasyon
    if (correct + wrong > subjectInfo.totalQuestions || correct < 0 || wrong < 0) {
      return 0;
    }
    
    // Net hesaplama: Doğru - (Yanlış ÷ 3)
    const net = Math.max(0, correct - (wrong / 3));
    
    // Ağırlıklı puan hesaplama: Net × Katsayı
    const weightedScore = net * subjectInfo.coefficient;
    
    return Math.round(weightedScore * 100) / 100; // 2 ondalık basamak
  };

  // Net Hesaplama Fonksiyonu
  const calculateNet = (correct: number, wrong: number): number => {
    return Math.max(0, correct - (wrong / 3));
  };

  // Toplam LGS Puanı Hesaplama
  const calculateTotalLGSScore = (scores: { [key: string]: number }): number => {
    // Tüm ders puanlarını topla
    const totalWeightedScore = Object.values(scores).reduce((sum, score) => sum + score, 0);
    
    // Taban puanı ekle
    const finalScore = LGS_BASE_SCORE + totalWeightedScore;
    
    // 100-500 arasında sınırla
    return Math.max(100, Math.min(500, Math.round(finalScore * 100) / 100));
  };

  // Doğru/Yanlış değiştiğinde puanı otomatik hesapla
  const updateAnswersAndScore = (subject: string, correct: number, wrong: number) => {
    const subjectInfo = LGS_SCORING[subject as keyof typeof LGS_SCORING];
    
    // Validasyon: Doğru + Yanlış toplam soru sayısını geçemez
    if (correct + wrong > subjectInfo.totalQuestions) {
      // Eğer toplam geçerse, yeni girilen değeri sınırla
      if (correct > subjectInfo.totalQuestions) {
        correct = subjectInfo.totalQuestions;
        wrong = 0;
      } else {
        wrong = subjectInfo.totalQuestions - correct;
      }
    }
    
    const score = calculateLGSScore(correct, wrong, subject);
    
    setNewResult(prev => ({
      ...prev,
      correctAnswers: { ...prev.correctAnswers, [subject]: correct },
      wrongAnswers: { ...prev.wrongAnswers, [subject]: wrong },
      scores: { ...prev.scores, [subject]: score }
    }));
  };

  // Demo data - Resmi LGS Formülü ile hesaplanmış
  useEffect(() => {
    const demoData: ExamResult[] = [
      {
        id: '1',
        examName: 'Karekök LGS Deneme 15',
        date: '2024-01-20',
        type: 'Deneme',
        institution: 'Karekök Yayınları',
        scores: {
          'Türkçe': 71.26, // (17 - 2/3) * 4.349 = 16.33 * 4.349 = 71.06
          'Matematik': 59.56, // (15 - 3/3) * 4.254 = 14 * 4.254 = 59.56
          'Fen Bilimleri': 57.72, // (15 - 3/3) * 4.123 = 14 * 4.123 = 57.72
          'T.C. İnkılap Tarihi': 14.45, // (9 - 1/3) * 1.667 = 8.67 * 1.667 = 14.45
          'İngilizce': 11.06, // (8 - 2/3) * 1.508 = 7.33 * 1.508 = 11.06
          'Din Kültürü': 16.46 // (9 - 1/3) * 1.899 = 8.67 * 1.899 = 16.46
        },
        correctAnswers: {
          'Türkçe': 17,
          'Matematik': 15,
          'Fen Bilimleri': 15,
          'T.C. İnkılap Tarihi': 9,
          'İngilizce': 8,
          'Din Kültürü': 9
        },
        wrongAnswers: {
          'Türkçe': 2,
          'Matematik': 3,
          'Fen Bilimleri': 3,
          'T.C. İnkılap Tarihi': 1,
          'İngilizce': 2,
          'Din Kültürü': 1
        },
        totalScore: 425.26, // 194.752 + 71.26 + 59.56 + 57.72 + 14.45 + 11.06 + 16.46 = 425.26
        ranking: 1250,
        totalParticipants: 15000
      },
      {
        id: '2',
        examName: 'Biltest LGS Deneme 8',
        date: '2024-01-15',
        type: 'Deneme',
        institution: 'Biltest Yayınları',
        scores: {
          'Türkçe': 60.89, // (15 - 3/3) * 4.349 = 14 * 4.349 = 60.89
          'Matematik': 53.88, // (14 - 4/3) * 4.254 = 12.67 * 4.254 = 53.88
          'Fen Bilimleri': 52.24, // (14 - 4/3) * 4.123 = 12.67 * 4.123 = 52.24
          'T.C. İnkılap Tarihi': 12.22, // (8 - 2/3) * 1.667 = 7.33 * 1.667 = 12.22
          'İngilizce': 7.54, // (6 - 3/3) * 1.508 = 5 * 1.508 = 7.54
          'Din Kültürü': 13.92 // (8 - 2/3) * 1.899 = 7.33 * 1.899 = 13.92
        },
        correctAnswers: {
          'Türkçe': 15,
          'Matematik': 14,
          'Fen Bilimleri': 14,
          'T.C. İnkılap Tarihi': 8,
          'İngilizce': 6,
          'Din Kültürü': 8
        },
        wrongAnswers: {
          'Türkçe': 3,
          'Matematik': 4,
          'Fen Bilimleri': 4,
          'T.C. İnkılap Tarihi': 2,
          'İngilizce': 3,
          'Din Kültürü': 2
        },
        totalScore: 395.44, // 194.752 + 60.89 + 53.88 + 52.24 + 12.22 + 7.54 + 13.92 = 395.44
        ranking: 1580,
        totalParticipants: 12500
      }
    ];

    setExamResults(demoData);
    setFilteredResults(demoData);
    calculateStats(demoData);
  }, []);

  // Calculate statistics
  const calculateStats = (results: ExamResult[]) => {
    if (results.length === 0) {
      setStats({
        totalExams: 0,
        averageScore: 0,
        bestScore: 0,
        worstScore: 0,
        improvementRate: 0,
        subjectAverages: {}
      });
      return;
    }

    const totalScore = results.reduce((sum, result) => sum + result.totalScore, 0);
    const averageScore = totalScore / results.length;
    const bestScore = Math.max(...results.map(r => r.totalScore));
    const worstScore = Math.min(...results.map(r => r.totalScore));

    // Calculate improvement rate
    let improvementRate = 0;
    if (results.length >= 2) {
      const recent = results.slice(-3);
      const older = results.slice(0, -3);
      if (older.length > 0) {
        const recentAvg = recent.reduce((sum, r) => sum + r.totalScore, 0) / recent.length;
        const olderAvg = older.reduce((sum, r) => sum + r.totalScore, 0) / older.length;
        improvementRate = ((recentAvg - olderAvg) / olderAvg) * 100;
      }
    }

    // Calculate subject averages
    const subjectAverages: { [key: string]: number } = {};
    SUBJECTS.forEach(subject => {
      const total = results.reduce((sum, result) => sum + result.scores[subject as keyof typeof result.scores], 0);
      subjectAverages[subject] = total / results.length;
    });

    setStats({
      totalExams: results.length,
      averageScore,
      bestScore,
      worstScore,
      improvementRate,
      subjectAverages
    });
  };

  // Filter and search
  useEffect(() => {
    let filtered = examResults;

    if (searchTerm) {
      filtered = filtered.filter(result =>
        result.examName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        result.institution?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (selectedType !== 'Tümü') {
      filtered = filtered.filter(result => result.type === selectedType);
    }

    setFilteredResults(filtered);
  }, [examResults, searchTerm, selectedType]);

  // Add new exam result
  const addExamResult = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Yeni resmi LGS puan hesaplama sistemiyle
    const totalScore = calculateTotalLGSScore(newResult.scores);
    
    const newExamResult: ExamResult = {
      ...newResult,
      id: Date.now().toString(),
      totalScore
    };

    const updatedResults = [...examResults, newExamResult];
    setExamResults(updatedResults);
    calculateStats(updatedResults);
    setShowAddModal(false);
    resetForm();
  };

  const resetForm = () => {
    setNewResult({
      examName: '',
      date: new Date().toISOString().split('T')[0],
      type: 'Deneme',
      institution: '',
      scores: {
        'Türkçe': 0,
        'Matematik': 0,
        'Fen Bilimleri': 0,
        'T.C. İnkılap Tarihi': 0,
        'İngilizce': 0,
        'Din Kültürü': 0
      },
      correctAnswers: {
        'Türkçe': 0,
        'Matematik': 0,
        'Fen Bilimleri': 0,
        'T.C. İnkılap Tarihi': 0,
        'İngilizce': 0,
        'Din Kültürü': 0
      },
      wrongAnswers: {
        'Türkçe': 0,
        'Matematik': 0,
        'Fen Bilimleri': 0,
        'T.C. İnkılap Tarihi': 0,
        'İngilizce': 0,
        'Din Kültürü': 0
      },
      ranking: undefined,
      totalParticipants: undefined
    });
  };

  const deleteResult = (id: string) => {
    const updatedResults = examResults.filter(result => result.id !== id);
    setExamResults(updatedResults);
    calculateStats(updatedResults);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('tr-TR');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-gray-900 dark:via-blue-900/20 dark:to-indigo-900/20 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Modern Header */}
        <div className="mb-12">
          <div className="relative overflow-hidden bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 rounded-3xl p-8 text-white">
            <div className="absolute inset-0 bg-black/10"></div>
            <div className="relative z-10">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                <div>
                  <h1 className="text-4xl font-bold mb-2 bg-gradient-to-r from-white to-blue-100 bg-clip-text text-transparent">
                    Sınav Sonuçları
                  </h1>
                  <p className="text-blue-100 text-lg">
                    LGS yolculuğunuzda başarıyı takip edin
                  </p>
                </div>
                <button
                  onClick={() => setShowAddModal(true)}
                  className="group relative overflow-hidden bg-white/20 backdrop-blur-sm border border-white/30 text-white px-6 py-3 rounded-2xl hover:bg-white/30 transition-all duration-300 transform hover:scale-105 mt-4 md:mt-0 flex items-center gap-2"
                >
                  <Plus size={20} className="group-hover:rotate-90 transition-transform duration-300" />
                  Yeni Sonuç Ekle
                </button>
              </div>
            </div>
            
            {/* Decorative Elements */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-white/10 to-transparent rounded-full -translate-y-32 translate-x-32"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-white/5 to-transparent rounded-full translate-y-24 -translate-x-24"></div>
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-6">
          <div className="border-b border-gray-200 dark:border-gray-700">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('results')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'results'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                <div className="flex items-center gap-2">
                  <BookOpen size={16} />
                  Sonuçlar
                </div>
              </button>
              <button
                onClick={() => setActiveTab('stats')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'stats'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                <div className="flex items-center gap-2">
                  <BarChart3 size={16} />
                  İstatistikler
                </div>
              </button>
              <button
                onClick={() => setActiveTab('chart')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'chart'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                <div className="flex items-center gap-2">
                  <LineChart size={16} />
                  Grafik
                </div>
              </button>
            </nav>
          </div>
        </div>

        {/* Results Tab */}
        {activeTab === 'results' && (
          <div>
            {/* Filters */}
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 mb-6 shadow-sm">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                    <input
                      type="text"
                      placeholder="Sınav adı veya kurum ara..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                </div>
                <div className="flex gap-4">
                  <select
                    value={selectedType}
                    onChange={(e) => setSelectedType(e.target.value)}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  >
                    <option value="Tümü">Tüm Sınavlar</option>
                    <option value="Deneme">Deneme Sınavları</option>
                    <option value="LGS">LGS</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Modern Results List */}
            <div className="space-y-6">
              {filteredResults.length === 0 ? (
                <div className="relative overflow-hidden bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-3xl p-12 text-center shadow-xl border border-white/20">
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-purple-500/5"></div>
                  <div className="relative z-10">
                    <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-blue-500 to-purple-500 rounded-2xl flex items-center justify-center">
                      <BookOpen className="text-white" size={32} />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
                      Henüz sınav sonucu yok
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 mb-8 text-lg">
                      İlk sınav sonucunuzu ekleyerek LGS yolculuğunuza başlayın
                    </p>
                    <button
                      onClick={() => setShowAddModal(true)}
                      className="group relative overflow-hidden bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-2xl hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                    >
                      <div className="flex items-center gap-3">
                        <Plus size={20} className="group-hover:rotate-90 transition-transform duration-300" />
                        İlk Sonucunu Ekle
                      </div>
                    </button>
                  </div>
                </div>
              ) : (
                filteredResults.map((result, index) => (
                  <div key={result.id} className="group relative overflow-hidden bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-3xl shadow-xl border border-white/20 hover:shadow-2xl transition-all duration-500 hover:-translate-y-2">
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-purple-500/5 to-indigo-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                    
                    {/* Header */}
                    <div className="relative z-10 p-8">
                      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between mb-6">
                        <div className="flex-1">
                          <div className="flex items-center gap-4 mb-4">
                            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl flex items-center justify-center text-white font-bold text-lg">
                              {index + 1}
                            </div>
                            <div>
                              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                                {result.examName}
                              </h3>
                              <div className="flex flex-wrap items-center gap-4 text-sm">
                                <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                                  <Calendar size={16} />
                                  {formatDate(result.date)}
                                </div>
                                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                  result.type === 'LGS' 
                                    ? 'bg-gradient-to-r from-purple-500 to-violet-500 text-white'
                                    : 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white'
                                }`}>
                                  {result.type}
                                </span>
                                {result.institution && (
                                  <span className="text-gray-500 dark:text-gray-400 font-medium">{result.institution}</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Score Display */}
                        <div className="flex items-center gap-6 mt-4 lg:mt-0">
                          <div className="text-center">
                            <div className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-1">
                              {result.totalScore.toFixed(1)}
                            </div>
                            <div className="text-sm text-gray-600 dark:text-gray-400 font-medium">
                              / 500 puan
                            </div>
                            {result.ranking && result.totalParticipants && (
                              <div className="mt-2 flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400">
                                <Users size={14} />
                                {result.ranking}/{result.totalParticipants}
                              </div>
                            )}
                          </div>
                          
                          {/* Progress Circle */}
                          <div className="relative w-20 h-20">
                            <svg className="w-20 h-20 transform -rotate-90" viewBox="0 0 36 36">
                              <path
                                className="text-gray-200 dark:text-gray-700"
                                d="M18 2.0845
                                  a 15.9155 15.9155 0 0 1 0 31.831
                                  a 15.9155 15.9155 0 0 1 0 -31.831"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                              />
                              <path
                                className="text-blue-500"
                                d="M18 2.0845
                                  a 15.9155 15.9155 0 0 1 0 31.831
                                  a 15.9155 15.9155 0 0 1 0 -31.831"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeDasharray={`${(result.totalScore / 500) * 100}, 100`}
                              />
                            </svg>
                            <div className="absolute inset-0 flex items-center justify-center">
                              <span className="text-sm font-bold text-gray-900 dark:text-white">
                                %{Math.round((result.totalScore / 500) * 100)}
                              </span>
                            </div>
                          </div>
                          
                          {/* Actions */}
                          <div className="flex flex-col gap-2">
                            <button
                              onClick={() => deleteResult(result.id)}
                              className="p-3 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-all duration-300 hover:scale-110"
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Subject Scores */}
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                        {SUBJECTS.map((subject) => {
                          const SubjectIcon = SUBJECT_ICONS[subject as keyof typeof SUBJECT_ICONS];
                          const score = result.scores[subject as keyof typeof result.scores];
                          const maxScore = LGS_SCORING[subject as keyof typeof LGS_SCORING].maxScore;
                          const percentage = (score / maxScore) * 100;
                          
                          return (
                            <div key={subject} className="group/subject relative overflow-hidden bg-white/60 dark:bg-gray-700/60 backdrop-blur-sm rounded-2xl p-4 border border-white/30 hover:shadow-lg transition-all duration-300">
                              <div className={`absolute inset-0 bg-gradient-to-br ${SUBJECT_COLORS[subject as keyof typeof SUBJECT_COLORS]} opacity-10 group-hover/subject:opacity-20 transition-opacity duration-300`}></div>
                              
                              <div className="relative z-10 text-center">
                                <div className={`w-10 h-10 mx-auto mb-3 bg-gradient-to-br ${SUBJECT_COLORS[subject as keyof typeof SUBJECT_COLORS]} rounded-xl flex items-center justify-center text-white shadow-lg`}>
                                  <SubjectIcon size={20} />
                                </div>
                                
                                <div className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2 truncate">
                                  {subject}
                                </div>
                                
                                <div className="text-xl font-bold text-gray-900 dark:text-white mb-1">
                                  {score.toFixed(1)}
                                </div>
                                
                                <div className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                                  {result.correctAnswers[subject as keyof typeof result.correctAnswers]}D / {result.wrongAnswers[subject as keyof typeof result.wrongAnswers]}Y
                                </div>
                                
                                {/* Mini Progress Bar */}
                                <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2 overflow-hidden">
                                  <div 
                                    className={`h-full bg-gradient-to-r ${SUBJECT_COLORS[subject as keyof typeof SUBJECT_COLORS]} rounded-full transition-all duration-500`}
                                    style={{ width: `${Math.min(percentage, 100)}%` }}
                                  ></div>
                                </div>
                                
                                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                  %{Math.round(percentage)}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Stats Tab */}
        {activeTab === 'stats' && (
          <div className="space-y-6">
            {/* Modern Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="group relative overflow-hidden bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-white/20 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-cyan-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-4">
                    <div className="p-3 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl shadow-lg">
                      <BookOpen className="h-6 w-6 text-white" />
                    </div>
                    <div className="text-right">
                      <p className="text-3xl font-bold text-gray-900 dark:text-white">{stats.totalExams}</p>
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Toplam Sınav</p>
                    </div>
                  </div>
                  <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full" style={{width: `${Math.min(stats.totalExams * 10, 100)}%`}}></div>
                  </div>
                </div>
              </div>

              <div className="group relative overflow-hidden bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-white/20 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <div className="absolute inset-0 bg-gradient-to-br from-green-500/10 to-emerald-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-4">
                    <div className="p-3 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl shadow-lg">
                      <Target className="h-6 w-6 text-white" />
                    </div>
                    <div className="text-right">
                      <p className="text-3xl font-bold text-gray-900 dark:text-white">{stats.averageScore.toFixed(1)}</p>
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Ortalama Puan</p>
                    </div>
                  </div>
                  <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-green-500 to-emerald-500 rounded-full" 
                         style={{width: `${stats.averageScore / 500 * 100}%`}}></div>
                  </div>
                </div>
              </div>

              <div className="group relative overflow-hidden bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-white/20 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <div className="absolute inset-0 bg-gradient-to-br from-yellow-500/10 to-orange-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-4">
                    <div className="p-3 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-xl shadow-lg">
                      <Award className="h-6 w-6 text-white" />
                    </div>
                    <div className="text-right">
                      <p className="text-3xl font-bold text-gray-900 dark:text-white">{stats.bestScore.toFixed(1)}</p>
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400">En Yüksek Puan</p>
                    </div>
                  </div>
                  <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-yellow-500 to-orange-500 rounded-full" 
                         style={{width: `${stats.bestScore / 500 * 100}%`}}></div>
                  </div>
                </div>
              </div>

              <div className="group relative overflow-hidden bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-white/20 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 to-violet-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-4">
                    <div className={`p-3 rounded-xl shadow-lg ${stats.improvementRate >= 0 ? 'bg-gradient-to-br from-green-500 to-emerald-500' : 'bg-gradient-to-br from-red-500 to-pink-500'}`}>
                      {stats.improvementRate >= 0 ? (
                        <TrendingUp className="h-6 w-6 text-white" />
                      ) : (
                        <TrendingDown className="h-6 w-6 text-white" />
                      )}
                    </div>
                    <div className="text-right">
                      <p className={`text-3xl font-bold ${stats.improvementRate >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {stats.improvementRate >= 0 ? '+' : ''}{stats.improvementRate.toFixed(1)}%
                      </p>
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Gelişim Oranı</p>
                    </div>
                  </div>
                  <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${stats.improvementRate >= 0 ? 'bg-gradient-to-r from-green-500 to-emerald-500' : 'bg-gradient-to-r from-red-500 to-pink-500'}`} 
                         style={{width: `${Math.min(Math.abs(stats.improvementRate) * 2, 100)}%`}}></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Subject Averages */}
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">Ders Ortalamaları</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {SUBJECTS.map((subject) => (
                  <div key={subject} className="text-center">
                    <div className={`px-4 py-3 rounded-lg text-sm font-medium mb-3 ${
                      SUBJECT_COLORS[subject as keyof typeof SUBJECT_COLORS]
                    }`}>
                      {subject}
                    </div>
                    <div className="text-2xl font-bold text-gray-900 dark:text-white">
                      {stats.subjectAverages[subject]?.toFixed(1) || '0.0'}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Modern Chart Tab */}
        {activeTab === 'chart' && (
          <div className="space-y-8">
            {examResults.length === 0 ? (
              <div className="relative overflow-hidden bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-3xl p-16 text-center shadow-xl border border-white/20">
                <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 to-purple-500/5"></div>
                <div className="relative z-10">
                  <div className="w-24 h-24 mx-auto mb-8 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-3xl flex items-center justify-center">
                    <LineChart className="text-white" size={40} />
                  </div>
                  <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                    Grafik için veri yok
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 text-lg mb-8">
                    En az 2 sınav sonucu ekleyerek ilerleme grafiğinizi görüntüleyebilirsiniz
                  </p>
                  <button
                    onClick={() => setShowAddModal(true)}
                    className="group relative overflow-hidden bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-8 py-4 rounded-2xl hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                  >
                    <div className="flex items-center gap-3">
                      <Plus size={20} className="group-hover:rotate-90 transition-transform duration-300" />
                      İlk Sonucunu Ekle
                    </div>
                  </button>
                </div>
              </div>
            ) : (
              <>
                {/* Main Progress Chart */}
                <div className="relative overflow-hidden bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-white/20">
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-indigo-500/5 to-purple-500/5"></div>
                  
                  <div className="relative z-10">
                    <div className="flex items-center justify-between mb-8">
                      <div>
                        <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                          İlerleme Analizi
                        </h3>
                        <p className="text-gray-600 dark:text-gray-400 text-lg">
                          Sınav sonuçlarınızın detaylı analizi
                        </p>
                      </div>
                      
                      {/* Trend Indicator */}
                      {examResults.length > 1 && (() => {
                        const sorted = examResults.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
                        const first = sorted[0]?.totalScore || 0;
                        const last = sorted[sorted.length - 1]?.totalScore || 0;
                        const trend = last - first;
                        return (
                          <div className={`flex items-center gap-3 px-6 py-3 rounded-2xl ${
                            trend >= 0 
                              ? 'bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-200 dark:border-green-800' 
                              : 'bg-gradient-to-r from-red-500/10 to-pink-500/10 border border-red-200 dark:border-red-800'
                          }`}>
                            <div className={`p-2 rounded-xl ${
                              trend >= 0 
                                ? 'bg-gradient-to-br from-green-500 to-emerald-500' 
                                : 'bg-gradient-to-br from-red-500 to-pink-500'
                            }`}>
                              {trend >= 0 ? (
                                <TrendingUp className="text-white" size={20} />
                              ) : (
                                <TrendingDown className="text-white" size={20} />
                              )}
                            </div>
                            <div>
                              <div className={`text-2xl font-bold ${trend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {trend >= 0 ? '+' : ''}{trend.toFixed(1)}
                              </div>
                              <div className="text-sm text-gray-600 dark:text-gray-400">
                                Toplam Gelişim
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                    
                    {/* Enhanced Chart */}
                    <div className="relative h-[500px] mb-8 bg-white/50 dark:bg-gray-700/50 rounded-2xl p-6 backdrop-blur-sm">
                      <svg className="w-full h-full" viewBox="0 0 900 450">
                        {/* Enhanced Grid */}
                        <defs>
                          <linearGradient id="chartGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                            <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.3"/>
                            <stop offset="100%" stopColor="#3B82F6" stopOpacity="0.05"/>
                          </linearGradient>
                          <filter id="glow">
                            <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                            <feMerge> 
                              <feMergeNode in="coloredBlur"/>
                              <feMergeNode in="SourceGraphic"/>
                            </feMerge>
                          </filter>
                        </defs>
                        
                        {/* Background Grid */}
                        {[0, 100, 200, 300, 400, 500].map((value) => (
                          <line 
                            key={value}
                            x1="80" 
                            y1={400 - (value * 0.6)} 
                            x2="820" 
                            y2={400 - (value * 0.6)} 
                            stroke="currentColor" 
                            strokeWidth="1" 
                            className="text-gray-200 dark:text-gray-600"
                            opacity="0.3"
                          />
                        ))}
                        
                        {/* Y-axis labels */}
                        {[0, 100, 200, 300, 400, 500].map((value) => (
                          <text 
                            key={value}
                            x="70" 
                            y={405 - (value * 0.6)} 
                            textAnchor="end" 
                            className="text-sm font-medium fill-gray-600 dark:fill-gray-400"
                          >
                            {value}
                          </text>
                        ))}
                        
                        {/* Area under curve */}
                        {examResults.length > 1 && (
                          <polygon
                            fill="url(#chartGradient)"
                            points={`80,400 ${examResults
                              .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
                              .map((result, index) => {
                                const x = 80 + (index * (740 / (examResults.length - 1)));
                                const y = 400 - (result.totalScore * 0.6);
                                return `${x},${y}`;
                              })
                              .join(' ')} 820,400`}
                          />
                        )}
                        
                        {/* Main Chart Line */}
                        {examResults.length > 1 && (
                          <polyline
                            fill="none"
                            stroke="#3B82F6"
                            strokeWidth="4"
                            filter="url(#glow)"
                            points={examResults
                              .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
                              .map((result, index) => {
                                const x = 80 + (index * (740 / (examResults.length - 1)));
                                const y = 400 - (result.totalScore * 0.6);
                                return `${x},${y}`;
                              })
                              .join(' ')
                            }
                          />
                        )}
                        
                        {/* Enhanced Data Points */}
                        {examResults
                          .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
                          .map((result, index) => {
                            const x = 80 + (index * (740 / Math.max(1, examResults.length - 1)));
                            const y = 400 - (result.totalScore * 0.6);
                            return (
                              <g key={result.id}>
                                {/* Glow effect */}
                                <circle
                                  cx={x}
                                  cy={y}
                                  r="12"
                                  fill="#3B82F6"
                                  opacity="0.2"
                                />
                                {/* Main point */}
                                <circle
                                  cx={x}
                                  cy={y}
                                  r="8"
                                  fill="#3B82F6"
                                  stroke="white"
                                  strokeWidth="3"
                                  filter="url(#glow)"
                                />
                                {/* Score label */}
                                <rect
                                  x={x - 25}
                                  y={y - 35}
                                  width="50"
                                  height="20"
                                  rx="10"
                                  fill="white"
                                  stroke="#3B82F6"
                                  strokeWidth="2"
                                />
                                <text
                                  x={x}
                                  y={y - 22}
                                  textAnchor="middle"
                                  className="text-sm font-bold fill-blue-600"
                                >
                                  {result.totalScore.toFixed(0)}
                                </text>
                                {/* Date label */}
                                <text
                                  x={x}
                                  y={430}
                                  textAnchor="middle"
                                  className="text-sm font-medium fill-gray-600 dark:fill-gray-400"
                                >
                                  {new Date(result.date).toLocaleDateString('tr-TR', { month: 'short', day: 'numeric' })}
                                </text>
                              </g>
                            );
                          })}
                        
                        {/* Axis Labels */}
                        <text x="450" y="445" textAnchor="middle" className="text-lg font-bold fill-gray-700 dark:fill-gray-300">
                          Sınav Tarihleri
                        </text>
                        <text x="40" y="225" textAnchor="middle" className="text-lg font-bold fill-gray-700 dark:fill-gray-300" transform="rotate(-90, 40, 225)">
                          Toplam Puan
                        </text>
                      </svg>
                    </div>

                    {/* Chart Statistics */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                      <div className="text-center p-4 bg-white/60 dark:bg-gray-700/60 rounded-2xl backdrop-blur-sm">
                        <div className="text-3xl font-bold text-blue-600 mb-1">
                          {examResults.length}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          Toplam Sınav
                        </div>
                      </div>
                      
                      <div className="text-center p-4 bg-white/60 dark:bg-gray-700/60 rounded-2xl backdrop-blur-sm">
                        <div className="text-3xl font-bold text-green-600 mb-1">
                          {Math.max(...examResults.map(r => r.totalScore)).toFixed(0)}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          En Yüksek Puan
                        </div>
                      </div>
                      
                      <div className="text-center p-4 bg-white/60 dark:bg-gray-700/60 rounded-2xl backdrop-blur-sm">
                        <div className="text-3xl font-bold text-purple-600 mb-1">
                          {(examResults.reduce((sum, r) => sum + r.totalScore, 0) / examResults.length).toFixed(0)}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          Ortalama Puan
                        </div>
                      </div>
                      
                      <div className="text-center p-4 bg-white/60 dark:bg-gray-700/60 rounded-2xl backdrop-blur-sm">
                        <div className="text-3xl font-bold text-orange-600 mb-1">
                          {examResults[examResults.length - 1]?.totalScore.toFixed(0) || '0'}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          Son Puan
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Subject Progress Charts */}
                <div className="relative overflow-hidden bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-white/20">
                  <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 via-pink-500/5 to-indigo-500/5"></div>
                  
                  <div className="relative z-10">
                    <h4 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">Ders Bazında İlerleme</h4>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {SUBJECTS.map((subject) => {
                        const SubjectIcon = SUBJECT_ICONS[subject as keyof typeof SUBJECT_ICONS];
                        // Net değerlerini hesapla
                        const subjectNets = examResults.map(r => 
                          calculateNet(
                            r.correctAnswers[subject as keyof typeof r.correctAnswers],
                            r.wrongAnswers[subject as keyof typeof r.wrongAnswers]
                          )
                        );
                        const maxNet = Math.max(...subjectNets);
                        const avgNet = subjectNets.reduce((sum, net) => sum + net, 0) / subjectNets.length;
                        const maxPossibleNet = LGS_SCORING[subject as keyof typeof LGS_SCORING].totalQuestions;
                        
                        return (
                          <div key={subject} className="group relative overflow-hidden bg-white/60 dark:bg-gray-700/60 backdrop-blur-sm rounded-2xl p-6 border border-white/30 hover:shadow-lg transition-all duration-300">
                            <div className={`absolute inset-0 bg-gradient-to-br ${SUBJECT_COLORS[subject as keyof typeof SUBJECT_COLORS]} opacity-5 group-hover:opacity-10 transition-opacity duration-300`}></div>
                            
                            <div className="relative z-10">
                              {/* Subject Header */}
                              <div className="flex items-center gap-3 mb-6">
                                <div className={`w-12 h-12 bg-gradient-to-br ${SUBJECT_COLORS[subject as keyof typeof SUBJECT_COLORS]} rounded-xl flex items-center justify-center text-white shadow-lg`}>
                                  <SubjectIcon size={24} />
                                </div>
                                <div>
                                  <h5 className="text-lg font-bold text-gray-900 dark:text-white">
                                    {subject}
                                  </h5>
                                  <p className="text-sm text-gray-600 dark:text-gray-400">
                                    Ortalama Net: {avgNet.toFixed(1)}
                                  </p>
                                </div>
                              </div>
                              
                              {/* Mini Chart */}
                              <div className="relative h-32 mb-4 bg-white/50 dark:bg-gray-600/50 rounded-xl p-3">
                                <svg className="w-full h-full" viewBox="0 0 300 120">
                                  <defs>
                                    <linearGradient id={`${subject}Gradient`} x1="0%" y1="0%" x2="0%" y2="100%">
                                      <stop offset="0%" stopColor={
                                        SUBJECT_COLORS[subject as keyof typeof SUBJECT_COLORS].includes('blue') ? '#3B82F6' : 
                                        SUBJECT_COLORS[subject as keyof typeof SUBJECT_COLORS].includes('green') ? '#10B981' :
                                        SUBJECT_COLORS[subject as keyof typeof SUBJECT_COLORS].includes('purple') ? '#8B5CF6' :
                                        SUBJECT_COLORS[subject as keyof typeof SUBJECT_COLORS].includes('red') ? '#EF4444' :
                                        SUBJECT_COLORS[subject as keyof typeof SUBJECT_COLORS].includes('yellow') ? '#F59E0B' : '#6B7280'
                                      } stopOpacity="0.3" />
                                      <stop offset="100%" stopColor={
                                        SUBJECT_COLORS[subject as keyof typeof SUBJECT_COLORS].includes('blue') ? '#3B82F6' : 
                                        SUBJECT_COLORS[subject as keyof typeof SUBJECT_COLORS].includes('green') ? '#10B981' :
                                        SUBJECT_COLORS[subject as keyof typeof SUBJECT_COLORS].includes('purple') ? '#8B5CF6' :
                                        SUBJECT_COLORS[subject as keyof typeof SUBJECT_COLORS].includes('red') ? '#EF4444' :
                                        SUBJECT_COLORS[subject as keyof typeof SUBJECT_COLORS].includes('yellow') ? '#F59E0B' : '#6B7280'
                                      } stopOpacity="0.1" />
                                    </linearGradient>
                                  </defs>
                                  {/* Grid lines */}
                                  {[0, 5, 10, 15, 20].map((value) => (
                                    <line 
                                      key={value}
                                      x1="30" 
                                      y1={100 - (value / maxPossibleNet) * 80} 
                                      x2="270" 
                                      y2={100 - (value / maxPossibleNet) * 80} 
                                      stroke="currentColor" 
                                      strokeWidth="1" 
                                      className="text-gray-200 dark:text-gray-500"
                                      opacity="0.3"
                                    />
                                  ))}
                                  
                                  {/* Area under curve */}
                                  {examResults.length > 1 && (
                                    <polygon
                                      fill={`url(#${subject}Gradient)`}
                                      points={`30,100 ${examResults
                                        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
                                        .map((result, index) => {
                                          const x = 30 + (index * (240 / (examResults.length - 1)));
                                          const net = calculateNet(
                                            result.correctAnswers[subject as keyof typeof result.correctAnswers],
                                            result.wrongAnswers[subject as keyof typeof result.wrongAnswers]
                                          );
                                          const y = 100 - (net / maxPossibleNet) * 80;
                                          return `${x},${y}`;
                                        })
                                        .join(' ')} 270,100`}
                                    />
                                  )}
                                  
                                  {/* Chart line */}
                                  {examResults.length > 1 && (
                                    <polyline
                                      fill="none"
                                      stroke={SUBJECT_COLORS[subject as keyof typeof SUBJECT_COLORS].includes('blue') ? '#3B82F6' : 
                                             SUBJECT_COLORS[subject as keyof typeof SUBJECT_COLORS].includes('green') ? '#10B981' :
                                             SUBJECT_COLORS[subject as keyof typeof SUBJECT_COLORS].includes('purple') ? '#8B5CF6' :
                                             SUBJECT_COLORS[subject as keyof typeof SUBJECT_COLORS].includes('red') ? '#EF4444' :
                                             SUBJECT_COLORS[subject as keyof typeof SUBJECT_COLORS].includes('yellow') ? '#F59E0B' : '#6B7280'}
                                      strokeWidth="3"
                                      points={examResults
                                        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
                                        .map((result, index) => {
                                          const x = 30 + (index * (240 / (examResults.length - 1)));
                                          const net = calculateNet(
                                            result.correctAnswers[subject as keyof typeof result.correctAnswers],
                                            result.wrongAnswers[subject as keyof typeof result.wrongAnswers]
                                          );
                                          const y = 100 - (net / maxPossibleNet) * 80;
                                          return `${x},${y}`;
                                        })
                                        .join(' ')
                                      }
                                    />
                                  )}
                                  
                                  {/* Data points */}
                                  {examResults
                                    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
                                    .map((result, index) => {
                                      const x = 30 + (index * (240 / Math.max(1, examResults.length - 1)));
                                      const net = calculateNet(
                                        result.correctAnswers[subject as keyof typeof result.correctAnswers],
                                        result.wrongAnswers[subject as keyof typeof result.wrongAnswers]
                                      );
                                      const y = 100 - (net / maxPossibleNet) * 80;
                                      return (
                                        <circle
                                          key={result.id}
                                          cx={x}
                                          cy={y}
                                          r="4"
                                          fill="white"
                                          stroke={SUBJECT_COLORS[subject as keyof typeof SUBJECT_COLORS].includes('blue') ? '#3B82F6' : 
                                                 SUBJECT_COLORS[subject as keyof typeof SUBJECT_COLORS].includes('green') ? '#10B981' :
                                                 SUBJECT_COLORS[subject as keyof typeof SUBJECT_COLORS].includes('purple') ? '#8B5CF6' :
                                                 SUBJECT_COLORS[subject as keyof typeof SUBJECT_COLORS].includes('red') ? '#EF4444' :
                                                 SUBJECT_COLORS[subject as keyof typeof SUBJECT_COLORS].includes('yellow') ? '#F59E0B' : '#6B7280'}
                                          strokeWidth="2"
                                        />
                                      );
                                    })}
                                </svg>
                              </div>
                              
                              {/* Stats */}
                              <div className="grid grid-cols-2 gap-4 text-center">
                                <div>
                                  <div className="text-xl font-bold text-gray-900 dark:text-white">
                                    {maxNet.toFixed(1)}
                                  </div>
                                  <div className="text-xs text-gray-600 dark:text-gray-400">
                                    En Yüksek Net
                                  </div>
                                </div>
                                <div>
                                  <div className="text-xl font-bold text-gray-900 dark:text-white">
                                    {subjectNets[subjectNets.length - 1]?.toFixed(1) || '0.0'}
                                  </div>
                                  <div className="text-xs text-gray-600 dark:text-gray-400">
                                    Son Net
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Modern Add Result Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="relative overflow-hidden bg-white/95 dark:bg-gray-800/95 backdrop-blur-md rounded-3xl w-full max-w-5xl max-h-[95vh] overflow-y-auto shadow-2xl border border-white/20">
              {/* Background Gradient */}
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-purple-500/5 to-indigo-500/5"></div>
              
              <div className="relative z-10 p-8">
                {/* Header */}
                <div className="flex justify-between items-center mb-8">
                  <div>
                    <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                      Yeni Sınav Sonucu
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      Sınav sonucunuzu ekleyerek ilerlemenizi takip edin
                    </p>
                  </div>
                  <button
                    onClick={() => setShowAddModal(false)}
                    className="group p-3 bg-white/80 dark:bg-gray-700/80 backdrop-blur-sm rounded-2xl hover:bg-red-50 dark:hover:bg-red-900/20 transition-all duration-300 border border-white/30"
                  >
                    <X className="text-gray-400 group-hover:text-red-500 transition-colors duration-300" size={24} />
                  </button>
                </div>

                <form onSubmit={addExamResult} className="space-y-8">
                  {/* Basic Info Section */}
                  <div className="relative overflow-hidden bg-white/60 dark:bg-gray-700/60 backdrop-blur-sm rounded-2xl p-6 border border-white/30">
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-indigo-500/5"></div>
                    <div className="relative z-10">
                      <h4 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
                        <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center">
                          <FileText className="text-white" size={16} />
                        </div>
                        Sınav Bilgileri
                      </h4>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                            Sınav Adı *
                          </label>
                          <input
                            type="text"
                            value={newResult.examName}
                            onChange={(e) => setNewResult({...newResult, examName: e.target.value})}
                            className="w-full px-4 py-3 bg-white/80 dark:bg-gray-600/80 backdrop-blur-sm border border-white/30 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 text-gray-900 dark:text-white placeholder-gray-500"
                            placeholder="Örn: 1. Deneme Sınavı"
                            required
                          />
                        </div>

                        <div className="space-y-2">
                          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                            Sınav Tarihi *
                          </label>
                          <input
                            type="date"
                            value={newResult.date}
                            onChange={(e) => setNewResult({...newResult, date: e.target.value})}
                            className="w-full px-4 py-3 bg-white/80 dark:bg-gray-600/80 backdrop-blur-sm border border-white/30 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 text-gray-900 dark:text-white"
                            required
                          />
                        </div>

                        <div className="space-y-2">
                          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                            Sınav Türü *
                          </label>
                          <select
                            value={newResult.type}
                            onChange={(e) => setNewResult({...newResult, type: e.target.value as 'LGS' | 'Deneme'})}
                            className="w-full px-4 py-3 bg-white/80 dark:bg-gray-600/80 backdrop-blur-sm border border-white/30 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 text-gray-900 dark:text-white"
                          >
                            <option value="Deneme">Deneme Sınavı</option>
                            <option value="LGS">LGS</option>
                          </select>
                        </div>

                        <div className="space-y-2">
                          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                            Kurum
                          </label>
                          <input
                            type="text"
                            value={newResult.institution}
                            onChange={(e) => setNewResult({...newResult, institution: e.target.value})}
                            className="w-full px-4 py-3 bg-white/80 dark:bg-gray-600/80 backdrop-blur-sm border border-white/30 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 text-gray-900 dark:text-white placeholder-gray-500"
                            placeholder="Örn: Karekök Yayınları"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Subject Scores Section */}
                  <div className="relative overflow-hidden bg-white/60 dark:bg-gray-700/60 backdrop-blur-sm rounded-2xl p-6 border border-white/30">
                    <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-pink-500/5"></div>
                    <div className="relative z-10">
                      <h4 className="text-xl font-bold text-gray-900 dark:text-white mb-2 flex items-center gap-3">
                        <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                          <Calculator className="text-white" size={16} />
                        </div>
                        Doğru/Yanlış Sayıları
                      </h4>
                      <p className="text-gray-600 dark:text-gray-400 mb-6">
                        Puanlar LGS sistemine göre otomatik hesaplanır (3 yanlış = 1 doğru)
                      </p>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {SUBJECTS.map((subject) => {
                          const SubjectIcon = SUBJECT_ICONS[subject as keyof typeof SUBJECT_ICONS];
                          const subjectInfo = LGS_SCORING[subject as keyof typeof LGS_SCORING];
                          const correct = newResult.correctAnswers[subject as keyof typeof newResult.correctAnswers];
                          const wrong = newResult.wrongAnswers[subject as keyof typeof newResult.wrongAnswers];
                          const score = newResult.scores[subject as keyof typeof newResult.scores];
                          const net = Math.max(0, correct - (wrong / 3));
                          
                          return (
                            <div key={subject} className="group relative overflow-hidden bg-white/80 dark:bg-gray-600/80 backdrop-blur-sm rounded-2xl p-5 border border-white/30 hover:shadow-lg transition-all duration-300">
                              <div className={`absolute inset-0 bg-gradient-to-br ${SUBJECT_COLORS[subject as keyof typeof SUBJECT_COLORS]} opacity-5 group-hover:opacity-10 transition-opacity duration-300`}></div>
                              
                              <div className="relative z-10">
                                {/* Subject Header */}
                                <div className="flex items-center gap-3 mb-4">
                                  <div className={`w-10 h-10 bg-gradient-to-br ${SUBJECT_COLORS[subject as keyof typeof SUBJECT_COLORS]} rounded-xl flex items-center justify-center text-white shadow-lg`}>
                                    <SubjectIcon size={20} />
                                  </div>
                                  <div>
                                    <h5 className="font-bold text-gray-900 dark:text-white">
                                      {subject}
                                    </h5>
                                    <p className="text-xs text-gray-600 dark:text-gray-400">
                                      {subjectInfo.totalQuestions} soru
                                    </p>
                                  </div>
                                </div>
                                
                                {/* Input Fields */}
                                <div className="grid grid-cols-2 gap-3 mb-4">
                                  <div>
                                    <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">
                                      Doğru
                                    </label>
                                    <input
                                      type="number"
                                      min="0"
                                      max={subjectInfo.totalQuestions}
                                      value={correct}
                                      onChange={(e) => {
                                        const newCorrect = parseInt(e.target.value) || 0;
                                        updateAnswersAndScore(subject, newCorrect, wrong);
                                      }}
                                      className="w-full px-3 py-2 text-sm bg-white/80 dark:bg-gray-500/80 backdrop-blur-sm border border-white/30 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 text-gray-900 dark:text-white text-center font-bold"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">
                                      Yanlış
                                    </label>
                                    <input
                                      type="number"
                                      min="0"
                                      max={subjectInfo.totalQuestions}
                                      value={wrong}
                                      onChange={(e) => {
                                        const newWrong = parseInt(e.target.value) || 0;
                                        updateAnswersAndScore(subject, correct, newWrong);
                                      }}
                                      className="w-full px-3 py-2 text-sm bg-white/80 dark:bg-gray-500/80 backdrop-blur-sm border border-white/30 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 text-gray-900 dark:text-white text-center font-bold"
                                    />
                                  </div>
                                </div>
                                
                                {/* Score Display */}
                                <div className="text-center space-y-2">
                                  <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                                    {score.toFixed(1)}
                                  </div>
                                  <div className="text-xs text-gray-500 dark:text-gray-400">
                                    Net: {net.toFixed(1)} • Puan: {score.toFixed(1)}
                                  </div>
                                  
                                  {/* Progress Bar */}
                                  <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                                    <div 
                                      className={`h-2 rounded-full bg-gradient-to-r ${SUBJECT_COLORS[subject as keyof typeof SUBJECT_COLORS]} transition-all duration-500`}
                                      style={{ width: `${Math.min(100, (score / subjectInfo.maxScore) * 100)}%` }}
                                    ></div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      
                      {/* Total Score Display */}
                      <div className="mt-8 relative overflow-hidden bg-gradient-to-br from-blue-500/10 to-purple-500/10 backdrop-blur-sm rounded-2xl p-6 border border-blue-200/30 dark:border-blue-800/30">
                        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-purple-500/5"></div>
                        <div className="relative z-10 text-center">
                          <div className="flex items-center justify-center gap-3 mb-4">
                            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-500 rounded-2xl flex items-center justify-center">
                              <Zap className="text-white" size={24} />
                            </div>
                            <div>
                              <h5 className="text-lg font-bold text-gray-900 dark:text-white">
                                Toplam Puan
                              </h5>
                              <p className="text-sm text-gray-600 dark:text-gray-400">
                                500 üzerinden
                              </p>
                            </div>
                          </div>
                          
                          <div className="text-5xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-4">
                            {calculateTotalLGSScore(newResult.scores).toFixed(1)}
                          </div>
                          
                          {/* Progress Circle */}
                          <div className="relative w-32 h-32 mx-auto">
                            <svg className="w-32 h-32 transform -rotate-90" viewBox="0 0 120 120">
                              <circle
                                cx="60"
                                cy="60"
                                r="50"
                                stroke="currentColor"
                                strokeWidth="8"
                                fill="none"
                                className="text-gray-200 dark:text-gray-600"
                              />
                              <circle
                                cx="60"
                                cy="60"
                                r="50"
                                stroke="url(#totalGradient)"
                                strokeWidth="8"
                                fill="none"
                                strokeLinecap="round"
                                strokeDasharray={`${(calculateTotalLGSScore(newResult.scores) / 500) * 314} 314`}
                                className="transition-all duration-1000"
                              />
                              <defs>
                                <linearGradient id="totalGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                  <stop offset="0%" stopColor="#3B82F6" />
                                  <stop offset="100%" stopColor="#8B5CF6" />
                                </linearGradient>
                              </defs>
                            </svg>
                            <div className="absolute inset-0 flex items-center justify-center">
                              <span className="text-lg font-bold text-gray-900 dark:text-white">
                                {((calculateTotalLGSScore(newResult.scores) / 500) * 100).toFixed(0)}%
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Ranking Section */}
                  <div className="relative overflow-hidden bg-white/60 dark:bg-gray-700/60 backdrop-blur-sm rounded-2xl p-6 border border-white/30">
                    <div className="absolute inset-0 bg-gradient-to-br from-green-500/5 to-emerald-500/5"></div>
                    <div className="relative z-10">
                      <h4 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
                        <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center">
                          <Trophy className="text-white" size={16} />
                        </div>
                        Sıralama Bilgileri
                      </h4>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                            Sıralama
                          </label>
                          <input
                            type="number"
                            min="1"
                            value={newResult.ranking || ''}
                            onChange={(e) => setNewResult({...newResult, ranking: parseInt(e.target.value) || undefined})}
                            className="w-full px-4 py-3 bg-white/80 dark:bg-gray-600/80 backdrop-blur-sm border border-white/30 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-300 text-gray-900 dark:text-white placeholder-gray-500"
                            placeholder="1250"
                          />
                        </div>

                        <div className="space-y-2">
                          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                            Toplam Katılımcı
                          </label>
                          <input
                            type="number"
                            min="1"
                            value={newResult.totalParticipants || ''}
                            onChange={(e) => setNewResult({...newResult, totalParticipants: parseInt(e.target.value) || undefined})}
                            className="w-full px-4 py-3 bg-white/80 dark:bg-gray-600/80 backdrop-blur-sm border border-white/30 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-300 text-gray-900 dark:text-white placeholder-gray-500"
                            placeholder="15000"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex justify-end space-x-4 pt-6">
                    <button
                      type="button"
                      onClick={() => setShowAddModal(false)}
                      className="group px-8 py-4 bg-white/80 dark:bg-gray-700/80 backdrop-blur-sm text-gray-700 dark:text-gray-300 rounded-2xl hover:bg-gray-100 dark:hover:bg-gray-600 transition-all duration-300 border border-white/30 font-semibold"
                    >
                      <div className="flex items-center gap-2">
                        <X size={20} className="group-hover:rotate-90 transition-transform duration-300" />
                        İptal
                      </div>
                    </button>
                    <button
                      type="submit"
                      className="group relative overflow-hidden px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-2xl hover:shadow-lg transition-all duration-300 transform hover:scale-105 font-semibold"
                    >
                      <div className="flex items-center gap-2">
                        <Save size={20} className="group-hover:scale-110 transition-transform duration-300" />
                        Sonucu Kaydet
                      </div>
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExamResults;